// Wave interference — the dominant field in the corpus. Chapter 01 sums five
// sources arranged in a ring plus one at the centre, each attenuated by
// exp(-d * k), and reads the result as height.
import { mulberry32 } from '../rng.js'
import { gradientAngle } from './gradient.js'

export default {
  id: 'interference',
  label: 'Interference',
  params: [
    { key: 'sources', label: 'sources', tier: 'structural', min: 2, max: 12, step: 1, default: 6 },
    { key: 'frequency', label: 'frequency', tier: 'continuous', min: 0.35, max: 3, step: 0.05, default: 1, format: 'x' },
    { key: 'amplitude', label: 'amplitude', tier: 'continuous', min: 0.1, max: 1.2, step: 0.05, default: 0.55 },
    { key: 'falloff', label: 'falloff', tier: 'continuous', min: 0, max: 3, step: 0.05, default: 1.15 },
    { key: 'drift', label: 'drift', tier: 'continuous', min: 0, max: 12, step: 0.1, default: 5 },
    // drift moves the waves; travel moves the sources that make them. Two
    // chapters turn on that difference — 09 shapes its walls with a wave that
    // sweeps, 27 drags a lit ball across a still cloud — and until now a
    // source was fixed wherever the seed put it for the life of the piece.
    { key: 'travel', label: 'travel', tier: 'continuous', min: 0, max: 1, step: 0.02, default: 0,
      hint: 'sources wander' },
  ],

  create(cfg, seed) {
    const rnd = mulberry32(seed)
    const srcs = []
    for (let i = 0; i < cfg.sources; i++) {
      const a = (i / cfg.sources) * Math.PI * 2 + (rnd() - 0.5) * 0.9
      const r = 0.5 + (rnd() - 0.5) * 0.4
      srcs.push({
        x: Math.cos(a) * r,
        y: Math.sin(a) * r,
        f: 1.8 + rnd() * 2.6,
        amp: 0.3 + rnd() * 0.22,
        ph: rnd() * Math.PI * 2,
      })
    }
    srcs.push({ x: 0, y: 0, f: 3, amp: 0.4, ph: 0 })

    // Each source's wander is derived from what it already carries rather than
    // drawn from the stream. Taking two more rnd() per source would have
    // shifted every subsequent value and changed every existing seed — the
    // same reason the stride-3 migration kept its calls in place.
    for (const s of srcs) {
      s.dr = 0.35 + ((s.f * 7.13) % 1) * 0.5
      s.dp = s.ph * 1.7
    }

    const sample = (x, y, t) => {
      let v = 0
      const tv = cfg.travel
      for (let i = 0; i < srcs.length; i++) {
        const s = srcs[i]
        // Unequal rates on the two axes, so a source traces a Lissajous curve
        // rather than a circle and the pattern never quite repeats.
        const sx = tv ? s.x + Math.cos(t * s.dr + s.dp) * tv * 0.55 : s.x
        const sy = tv ? s.y + Math.sin(t * s.dr * 0.83 + s.dp) * tv * 0.55 : s.y
        const dx = x - sx, dy = y - sy
        const d = Math.sqrt(dx * dx + dy * dy)
        v += Math.sin(d * s.f * cfg.frequency * 3 - t * cfg.drift + s.ph) *
             s.amp * Math.exp(-d * cfg.falloff)
      }
      return v * (cfg.amplitude / 0.55)
    }

    return { sample, gradient: (x, y, t) => gradientAngle(sample, x, y, t) }
  },
}
